import React, { createContext, useContext, useState, useMemo, useRef, useEffect, useCallback } from 'react';

import useDeepCompareRef from 'Hooks/useDeepCompareRef';

const HitCardPopupContext = createContext();

const HIDE_DELAY = 200;

export const HitCardPopupProvider = ({ children }) => {
  const [hit, setHit] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [config, setConfig] = useState(null);
  const hideTimeout = useRef(null);

  const cancelHide = useCallback(() => clearTimeout(hideTimeout.current), []);

  const showPopup = useCallback((product, options) => {
    cancelHide();
    setHit(product);
    setConfig(options);
  }, [cancelHide]);

  const hidePopup = useCallback(() => {
    cancelHide();
    setHit(null);
  }, [cancelHide]);

  const scheduleHide = useCallback(() => {
    cancelHide();
    hideTimeout.current = setTimeout(() => setHit(null), HIDE_DELAY);
  }, [cancelHide]);

  useEffect(() => cancelHide, [cancelHide]);

  const setPopupPosition = useCallback(coordinates => setPosition(coordinates), []);

  const stableHit = useDeepCompareRef(hit);
  const stablePosition = useDeepCompareRef(position);
  const stableConfig = useDeepCompareRef(config);

  const contextValue = useMemo(() => ({
    isOpen: !!stableHit?.slug,
    hit: stableHit,
    position: stablePosition,
    config: stableConfig,
    showPopup,
    hidePopup,
    scheduleHide,
    cancelHide,
    setPopupPosition,
  }), [
    stableHit, stablePosition, stableConfig,
    showPopup, hidePopup, scheduleHide, cancelHide, setPopupPosition,
  ]);

  return (
    <HitCardPopupContext.Provider
      value={contextValue}
    >
      {children}
    </HitCardPopupContext.Provider>
  );
};

export const useHitCardPopup = () => useContext(HitCardPopupContext);
